import {useCallback, useContext, useState} from 'react'

import Context from '@s-ui/react-context'

import {FINANCING_INTENTION_ID} from '../ContactForm/BuyerIntentions.js'
import {V2_FIELDS} from '../schema/contactSchema.js'
import {mapDomainErrorToFields} from '../schema/domainErrorMap.js'
import useAuthBeforeLead from './useAuthBeforeLead.js'

const INITIAL_RESULT = {
  hasContacted: false,
  messageContactId: undefined,
  hasGenericError: false,
  hasCaptchaError: false,
  fieldErrors: {}
}

export const useContactSubmit = ({
  adId,
  contractId,
  pageName,
  enableAuthBeforeLead = false,
  subscriptionCommitment,
  onContact
} = {}) => {
  const {domain, logger} = useContext(Context)
  const [result, setResult] = useState(INITIAL_RESULT)
  const {registerIfNeeded} = useAuthBeforeLead({enableAuthBeforeLead})

  const submitContact = useCallback(
    async (values, {captchaToken} = {}) => {
      const config = domain.get('config')
      const errors = config.get('ERRORS')
      const {CONTACT_DEFAULT_MESSAGE} = config.get('COMMON')

      const message = values[V2_FIELDS.MESSAGE] || CONTACT_DEFAULT_MESSAGE
      const buyerIntentions = values[V2_FIELDS.BUYER_INTENTIONS] ?? []

      const {userHasBeenRegistered = false, autoGeneratedPassword} = await registerIfNeeded({
        email: values[V2_FIELDS.EMAIL],
        name: values[V2_FIELDS.NAME]
      })

      const [error, response] = await domain.get('contact_seller_use_case').execute({
        adId,
        contractId,
        context: pageName,
        name: values[V2_FIELDS.NAME],
        email: values[V2_FIELDS.EMAIL],
        phone: values[V2_FIELDS.PHONE],
        message,
        hasAcceptedTermsAndConditions: values[V2_FIELDS.TERMS],
        zipCode: values[V2_FIELDS.ZIP_CODE],
        buyerIntentions,
        isDefaultMessage: message === CONTACT_DEFAULT_MESSAGE,
        subscriptionCommitment,
        captchaToken
      })

      if (error) {
        logger?.error({
          name: `[ContactSeller]: "adId: ${adId} | contractId: ${contractId}" error in page: ${pageName}`,
          message: `[ContactSeller]: ${error?._message}, status: ${error?._status}, error: ${JSON.stringify(
            error?._error
          )}`
        })

        const mapped = mapDomainErrorToFields({error, errors})
        setResult({...INITIAL_RESULT, ...mapped})

        return mapped
      }

      setResult({...INITIAL_RESULT, hasContacted: true, messageContactId: response?.messageContactId})

      onContact?.({
        message,
        buyerIntentions,
        isFinancingLead: buyerIntentions.includes(FINANCING_INTENTION_ID),
        messageContactId: response?.messageContactId,
        userId: response?.userId,
        name: values[V2_FIELDS.NAME],
        email: values[V2_FIELDS.EMAIL],
        phone: values[V2_FIELDS.PHONE],
        zipCode: values[V2_FIELDS.ZIP_CODE],
        enableAuthBeforeLead,
        userHasBeenRegistered,
        autoGeneratedPassword
      })

      return {fieldErrors: {}, hasGenericError: false, hasCaptchaError: false}
    },
    [
      adId,
      contractId,
      pageName,
      enableAuthBeforeLead,
      subscriptionCommitment,
      onContact,
      domain,
      logger,
      registerIfNeeded
    ]
  )

  const resetResult = useCallback(() => setResult(INITIAL_RESULT), [])

  const clearFieldError = useCallback(
    name =>
      setResult(current => {
        if (current.fieldErrors[name] === undefined) {
          return current
        }

        const fieldErrors = Object.fromEntries(Object.entries(current.fieldErrors).filter(([field]) => field !== name))

        return {...current, fieldErrors}
      }),
    []
  )

  return {submitContact, resetResult, clearFieldError, ...result}
}

export default useContactSubmit
